<template>
  <div>
    <b-card>
      <h2 class="text-purple font-weight-bold mb-1">
        <b-link class="btn btn-outline-primary btn-sm" to="/">
          <FeatherIcon icon="ChevronLeftIcon"/>
          {{ $t("back") }}
        </b-link>
        {{ $t("daily_transactions") }}
      </h2>

      <!-- Filters -->
      <b-form >
        <b-row>
          <!-- Start Date -->
          <b-col md="3" sm="6">
            <b-form-group label="Start Date:">
              <b-form-datepicker
                  v-model="startDate"
                  :max="new Date()"
                  :placeholder="$t('select_date')"
              />
            </b-form-group>
          </b-col>

          <!-- End Date -->
          <b-col md="3" sm="6">
            <b-form-group label="End Date:">
              <b-form-datepicker
                  v-model="endDate"
                  :max="new Date()"
              />
            </b-form-group>
          </b-col>

          <!-- Currencies -->
          <b-col md="3" sm="6">
            <b-form-group label="Currency:">
              <b-form-select
                  v-model="selectedCurrency"
                  :options="[
                    { value: null, text: $t('select_currency') },
                    ...currencyOptions
                  ]"
                  @change="getReport"
              />
            </b-form-group>
          </b-col>
          <b-col class="col-md-1">
            <b-button
                variant="secondary"
                @click="getReport"
                size="md"
                class="mt-2"
            >
              {{ $t('search') }}
            </b-button>
          </b-col>
          <b-col class="col-md-1">
            <b-button
                variant="secondary"
                size="md"
                class="mt-2"
                :disabled="exportLoading"
                @click="exportData"
            >
              <span v-if="!exportLoading">{{ $t('export') }}</span>
              <span v-else>{{ $t('exporting') }}…</span>
            </b-button>
          </b-col>
          <!-- Search button -->
        </b-row>
      </b-form>

      <!-- Table -->
      <Table
          :url="url"
          :fields="fields"
          :payload="tablePayload"
          :updateData="updateCounter"
          ref="table"
      >
        <template #cell(action)="data">
          {{ $t(data.item.action) }}
        </template>

        <template #cell(balance_before_change)="data">
          {{ getFormattedBalanceBefore(data.item) }}
        </template>

        <template #cell(balance_after_change)="data">
          {{ getFormattedBalanceAfter(data.item) }}
        </template>


        <template #cell(change)="data">
          {{ getFormattedBalanceChange(data.item) }}
        </template>


        <template #cell(date)="data">
          {{ formatDateTime(data.item.created_at) }}
        </template>
      </Table>
    </b-card>
  </div>
</template>

<script>
import Table from '@/components/common/Table2.vue'
import { CurrencyAmountFormat } from '@core/utils/CurrencyAmountFormat'

export default {
  name: 'DailyTransactions',
  components: { Table },
  data() {
    return {
      url: '/company/daily-transactions',
      startDate: null,
      endDate: null,
      selectedCurrency: null,
      currencyOptions: [],
      exportLoading: false,
      updateCounter: 0,
      fields: [
        { key: 'id',                   label: 'ID',                sortable: false },
        { key: 'action',               label: 'Action',            sortable: false },
        { key: 'balance_before_change',label: 'Balance Before',    sortable: false },
        { key: 'balance_after_change', label: 'Balance After',     sortable: false },
        { key: 'change',               label: 'Change' },
        { key: 'date',                 label: 'Date' },
      ],
    }
  },
  computed: {
    tablePayload() {
      const p = {}
      if (this.startDate) p.startDate  = this.startDate
      if (this.endDate) p.endDate    = this.endDate
      if (this.selectedCurrency !== null) {
        p.currencies = this.selectedCurrency
      }
      return p
    }
  },
  created() {
    const today = new Date()
    this.endDate   = today.toISOString().substr(0,10)
    this.loadCurrencies()
    this.getReport()
  },
  methods: {
    async exportData() {
      this.exportLoading = true
      try {
        const payload = {
          startDate: this.startDate,
          endDate: this.endDate,
          lang: this.$i18n?.locale,
        }
        if (this.selectedCurrency !== null) {
          payload.currencies = this.selectedCurrency
        }

        const response = await this.$http.get(
            '/company/daily-transactions/export',
            {
              params: payload,
              responseType: 'blob'
            }
        )

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        let filename = 'daily_transactions'

        if (this.startDate) filename += `_${this.startDate}`
        if (this.endDate) filename += `_${this.endDate}`

        filename += '.csv'

        link.setAttribute('download', filename)

        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error exporting daily transactions:', error)
      } finally {
        this.exportLoading = false
      }
    },

    async loadCurrencies() {
      try {
        this.$http.post("/currency").then((response) => {
          const res = response.data.value
          this.currencyOptions = res.map(c => ({
            value: c.id,
            text: c.name
          }))
        })
      } catch (e) {
        console.error('Failed to load currencies', e)
      }
    },

    getReport() {
      this.updateCounter++
    },

    amountFormat(a, c) {
      return CurrencyAmountFormat(a, c)
    },

    formatDateTime(s) {
      return new Date(s).toLocaleString(undefined, {
        year:   'numeric',
        month:  '2-digit',
        day:    '2-digit',
        hour:   '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    },
    getFormattedBalanceBefore(item) {
      const currency = item.currency?.name || '';
      const value =
          ['BTC', 'ETH', 'USDT'].includes(currency)
              ? item.crypto_balance_before_change ?? 0
              : item.balance_before_change ?? 0;

      return this.amountFormat(value, currency);
    },
    getFormattedBalanceAfter(item) {
      const currency = item.currency?.name || '';
      const value =
          ['BTC', 'ETH', 'USDT'].includes(currency)
              ? item.crypto_balance_after_change ?? 0
              : item.balance_after_change ?? 0;

      return this.amountFormat(value, currency);
    },
    getFormattedBalanceChange(item) {
      const currency = item.currency?.name || '';

      const after = ['BTC', 'ETH', 'USDT'].includes(currency)
          ? item.crypto_balance_after_change ?? 0
          : item.balance_after_change ?? 0;

      const before = ['BTC', 'ETH', 'USDT'].includes(currency)
          ? item.crypto_balance_before_change ?? 0
          : item.balance_before_change ?? 0;

      return this.amountFormat(after - before, currency);
    }
  }
}
</script>

<style scoped>
</style>
